import { Controller } from "@hotwired/stimulus"

// Disables the clicked element immediately to prevent double submits/clicks
export default class extends Controller {
  static values = { text: String }

  connect() {
    // Re-enable button when page loads (e.g., after validation errors)
    this.enable()
    
    // Listen for Turbo events to re-enable button if form submission fails
    document.addEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
  }

  disconnect() {
    document.removeEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this))
  }

  handleSubmitEnd(event) {
    // If the form submission was not successful (e.g., validation errors),
    // re-enable the button
    if (event.detail && !event.detail.success) {
      this.enable()
    }
  }

  enable() {
    const el = this.element
    
    // Find the submit button if this is a form
    let submitButton = null
    if (el.tagName === 'FORM') {
      submitButton = el.querySelector('button[type="submit"], input[type="submit"]')
    } else {
      submitButton = el
    }

    if (submitButton) {
      // Remove disabled state
      el.dataset.disabled = "false"
      submitButton.removeAttribute('disabled')
      submitButton.removeAttribute('aria-disabled')
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
      
      // Restore original text if it was changed
      if (this.hasTextValue && submitButton.dataset.originalText) {
        if (submitButton.tagName === 'INPUT' && (submitButton.type === 'submit' || submitButton.type === 'button')) {
          submitButton.value = submitButton.dataset.originalText
        } else if (submitButton.tagName === 'BUTTON') {
          submitButton.innerText = submitButton.dataset.originalText
        }
        delete submitButton.dataset.originalText
      }
    }
  }

  disable (event) {
    const el = this.element

    // already disabled → swallow subsequent clicks
    if (el.dataset.disabled === "true") {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    // Mark as disabled AFTER allowing the event to proceed
    // Use setTimeout to let Rails UJS handle the event first (for method: :post links)
    setTimeout(() => {
      el.dataset.disabled = "true"
    }, 0)

    // Find the submit button if this is a form
    let submitButton = null
    if (el.tagName === 'FORM') {
      submitButton = el.querySelector('button[type="submit"], input[type="submit"]')
    } else {
      submitButton = el
    }

    // set disabled styles/attrs
    if (submitButton) {
      submitButton.classList.add('opacity-50', 'cursor-not-allowed')
      submitButton.setAttribute('aria-disabled', 'true')
      
      // For buttons/inputs, set disabled attribute AFTER form submission starts
      // Use setTimeout to allow the form to submit first
      if (submitButton.tagName === 'BUTTON' || submitButton.tagName === 'INPUT') {
        // Don't disable immediately - let the form submit first
        setTimeout(() => {
          submitButton.setAttribute('disabled', 'disabled')
          submitButton.classList.add('pointer-events-none')
        }, 100)
      }
    }
    
    // For links, don't prevent pointer events immediately - let the navigation start
    // Instead, prevent subsequent clicks via event handling
    if (el.tagName === 'A') {
      // Disable pointer events after a short delay to allow navigation to start
      setTimeout(() => {
        el.classList.add('pointer-events-none')
      }, 50)
    }

    // replace text if provided
    if (this.hasTextValue && submitButton) {
      if (submitButton.tagName === 'INPUT' && (submitButton.type === 'submit' || submitButton.type === 'button')) {
        submitButton.value = this.textValue
      } else if (submitButton.tagName === 'BUTTON') {
        submitButton.dataset.originalText = submitButton.innerText
        submitButton.innerText = this.textValue
      }
    }
  }
}


